/**
 * @ngdoc class
 * @name ApiCallManager
 * @description
 * This class represent a ApiCallManager object.
 *
 * @memberof common
 */
class ApiCallManager {
  /**
   * Class constructor.
   * @param {Service}  appUtils          The service to get the page query param.
   * @param {Function} dataProcess       The method to process the content.
   * @param {Function} methodCall        The method to fecth the content from.
   * @param {Object}   methodCallParams  The params to call the method with.
   */
  constructor(
    appUtils,
    dataProcess,
    methodCall,
    methodCallParams,
  ) {
    /**
     * The local reference to the `appUtils` service.
     * @type {Object}
     */
    this.appUtils = appUtils;
    /**
     * The local reference to the `dataProcess` function.
     * @type {Function}
     */
    this.dataProcess = dataProcess || ((content) => content);
    /**
     * The local reference to the `methodCall` function.
     * @type {String}
     */
    this.methodCall = methodCall;
    /**
     * The local reference to the `methodCallParams` object.
     * @type {Object}
     */
    this.methodCallParams = methodCallParams || [];
    /**
     * The content promise to prevent the methodCall to be called twice
     * @type {?Promise}
     */
    this.contentPromise = null;
    /**
     * The pagination information.
     * @type {Object}
     */
    this.pagination = { links: {} };
    /**
     * Flag if a request to fetch the content is in progress.
     * @type {Boolean}
     */
    this.loading = false;
  }
  /**
   * Get the Content from the BE.
   * @param {Boolean} refresh flag to force a refresh of the Content.
   * @return {Promise}
   */
  _getContent(refresh) {
    if (this.contentPromise && !refresh) {
      return this.contentPromise;
    }

    this.contentPromise = this.methodCall(...this.methodCallParams)
    .then((response) => {
      this.pagination = this.appUtils.getPaginationFromResponse(response);
      return this.dataProcess(response.data.data);
    });

    return this.contentPromise;
  }
  /**
   * Get a Content by its Id.
   * @return {Promise}
   */
  _getContentById(contentId) {
    return this._getContent()
    .then((content) => {
      const el = content.find((item) => item.id === contentId);
      if (el) {
        // We have found the element, return it
        return el;
      } else if (this.pagination.links.next) {
        /**
         * We have not found the element,
         * but we can fetch more content and
         * search again
         */
        return this._loadMoreContent()
        .then(() => this._getContentById(contentId));
      }

      // We have not found the element, return an error
      return Promise.reject(new Error('Content could not be found'));
    });
  }
  /**
   * Load more Content from the BE.
   * @return {Promise}
   */
  _loadMoreContent() {
    return this._getContent()
    .then((content) => {
      const page = this.appUtils.getQueryParam(this.pagination.links.next, 'page');

      this.contentPromise = this.methodCall(...this.methodCallParams, page)
      .then((response) => {
        this.pagination = this.appUtils.getPaginationFromResponse(response);
        return [
          ...content,
          ...this.dataProcess(response.data.data),
        ];
      });
      return this.contentPromise;
    });
  }
  /**
   * Handle the load of the content.
   * @param {Boolean} first flag to get the Content
   *                  for the first time or load more.
   * @return {Promise}
   */
  load(first) {
    this.loading = true;
    const promise = first
      ? this._getContent()
      : this._loadMoreContent();

    return promise.then((content) => {
      this.loading = false;
      return content;
    });
  }
  /**
   * Handle the pagination has more data status.
   */
  hasMoreData() {
    return !this.loading && this.pagination.links.next;
  }
  /**
   * Handle the clear of the saved content.
   */
  clearContent() {
    this.contentPromise = null;
  }
}

export default ApiCallManager;



// WEBPACK FOOTER //
// ./src/frontend/modules/common/models/apiCallManager.js